/* Uses Prestige's original product form and the original Essential Upsell node. */
(() => {
  'use strict';
  if (customElements.get('lb-purchase-features')) return;
  const one = (root, selector) => root?.querySelector(selector);
  const all = (root, selector) => [...(root?.querySelectorAll(selector) || [])];
  const visible = el => !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  const setText = (el, text) => { if (el && el.textContent !== text) el.textContent = text; };
  const clean = text => (text || '').replace(/\s+/g, ' ').trim();
  const displayText = node => {
    if (!node) return '';
    const copy = node.cloneNode(true);
    all(copy, '.sr-only, [aria-hidden="true"]').forEach(el => el.remove());
    return clean(copy.textContent);
  };
  class PurchaseFeatures extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      if (!one(this, '.lb-purchase-bar') || (this.dataset.mode === 'product' && !one(this, 'template[data-lb-accordion]'))) {
        this.readyObserver ||= new MutationObserver(() => this.connectedCallback());
        this.readyObserver.observe(this, { childList: true, subtree: true });
        return;
      }
      this.readyObserver?.disconnect();
      this.controller = new AbortController();
      this.mobile = matchMedia('(max-width: 999px)');
      this.closed = new WeakSet();
      this.folded = new Map();
      this.sets = new Map();
      this.owned = new Set();
      this.sizeSummary = null;
      this.scentSummary = null;
      this.mode = this.dataset.mode;
      this.bar = one(this, '.lb-purchase-bar');
      if (!this.bar) return;
      this.barHome = document.createComment('lb-bar-home');
      this.bar.before(this.barHome);
      document.body.append(this.bar);
      this.reserve = document.createElement('div');
      this.reserve.className = 'lb-purchase-reserve';
      this.reserve.setAttribute('aria-hidden', 'true');
      document.body.append(this.reserve);
      this.support = one(this, '.lb-product-support');
      this.libraryFold = one(this, '.lb-library-fold');
      this.staging = one(this, '[data-lb-staging]');
      this.on = (node, name, fn, extra = {}) => node?.addEventListener(name, fn, { signal: this.controller.signal, ...extra });
      this.schedule = () => {
        if (this.frame || !this.isConnected) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.reconcile(); });
      };
      this.on(window, 'scroll', this.schedule, { passive: true });
      this.on(window, 'resize', this.schedule, { passive: true });
      this.on(window, 'pageshow', this.schedule);
      this.on(this.mobile, 'change', this.schedule);
      for (const name of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder', 'product:rerender', 'variant:change', 'cart:error', 'variant:add', 'dialog:after-show', 'dialog:after-hide', 'change', 'load']) {
        this.on(document, name, this.schedule, { capture: true });
      }
      this.on(document, 'cart:error', event => {
        if (this.mode === 'product' && event.target === this.form) {
          this.main?.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      });
      this.on(document, 'variant:change', event => {
        if (event.target === this.form) {
          this.variantMediaId = event.detail?.variant?.featured_media?.id || null;
          this.schedule();
        }
      });
      this.on(document, 'shopify:block:select', event => {
        const details = event.target?.closest?.('details') || one(event.target, 'details');
        if (details) { details.open = true; details.setAttribute('aria-expanded', 'true'); }
      });
      this.on(one(this.bar, '[data-lb-proxy]'), 'click', () => this.purchase());
      this.observer = new MutationObserver(this.schedule);
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(document.body);
      this.resizeObserver.observe(this.bar);
      this.watch();
      this.reconcile();
    }
    watch() {
      this.observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-busy', 'open', 'src', 'aria-current', 'hidden'] });
    }
    disconnectedCallback() {
      this.readyObserver?.disconnect();
      if (!this.controller) return;
      this.controller.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.restoreMobile();
      this.main?.classList.remove('lb-product-main');
      document.body.classList.remove('lb-product-active');
      this.owned.forEach(node => node.remove());
      if (this.barHome?.parentNode) this.barHome.replaceWith(this.bar);
      else this.bar?.remove();
      if (this.support && this.staging) this.staging.append(this.support);
      if (this.libraryFold && this.staging) this.staging.append(this.libraryFold);
      this.reserve?.remove();
      this.controller = null;
    }
    headerBottom() {
      const header = one(document, '#shopify-section-header, .shopify-section--header');
      return Math.max(0, header?.getBoundingClientRect().bottom || 0);
    }
    overlayOpen() {
      return all(document, 'dialog[open], [role="dialog"][open], [role="dialog"][aria-modal="true"], .drawer[open], .popover[open], .modal[open], header-sidebar[open], .pswp--open').some(visible);
    }
    reconcile() {
      if (!this.isConnected) return;
      this.observer.disconnect();
      try {
        if (this.mode === 'product') this.product();
        else this.collection();
      } finally { this.watch(); }
    }
    show(shouldShow) {
      this.bar.hidden = !shouldShow;
      const height = shouldShow ? this.bar.getBoundingClientRect().height : (this.lastHeight || 0);
      if (height) this.lastHeight = height;
      // Keep the footer reachable without changing height on every show/hide boundary.
      const reserve = this.mode === 'product' && !this.mobile.matches ? 0 : (this.lastHeight || 0);
      this.reserve.style.setProperty('--lb-purchase-reserve', `${reserve}px`);
    }
    closeOnce(root) {
      all(root, '.accordion details').forEach(details => {
        if (!this.closed.has(details)) {
          details.open = false;
          details.setAttribute('aria-expanded', 'false');
          this.closed.add(details);
        }
      });
    }
    makeFold(title, initiallyOpen = false) {
      const template = one(this, 'template[data-lb-accordion]');
      const wrapper = template.content.firstElementChild.cloneNode(true);
      setText(one(wrapper, '[data-lb-fold-title]'), title);
      const details = one(wrapper, 'details');
      details.open = initiallyOpen;
      details.setAttribute('aria-expanded', String(initiallyOpen));
      this.closed.add(details);
      return wrapper;
    }
    product() {
      const main = one(document, '.shopify-section--main-product');
      const list = one(main, '.product-info__block-list');
      const form = one(main, 'product-form form[action*="/cart/add"]');
      const original = one(form, 'buy-buttons button[type="submit"]');
      this.form = form; this.original = original;
      if (!main || !list) { this.show(false); return; }
      if (this.main && this.main !== main) {
        this.restoreMobile();
        this.main.classList.remove('lb-product-main');
      }
      this.main = main;
      main.classList.add('lb-product-main');
      document.body.classList.toggle('lb-product-active', this.mobile.matches);
      // A single shared Library section is used in both device layouts.
      if (this.libraryFold && this.libraryFold.parentNode !== list) list.append(this.libraryFold);
      if (this.support && this.support.parentNode !== list) list.append(this.support);
      const sameProduct = !this.dataset.productId || !form || (form.getAttribute('id') || '').startsWith(`product-form-main-${this.dataset.productId}-`);
      if (this.libraryFold) this.libraryFold.hidden = !sameProduct;
      if (this.support) this.support.hidden = !sameProduct;
      if (this.mobile.matches) {
        this.closeOnce(main);
        this.closeOnce(one(document, '.shopify-section--footer'));
        this.descriptionFold(main);
        this.summary(list);
        this.upsell(list);
      } else this.restoreMobile();
      if (!form || !original) { this.show(false); return; }
      const proxy = one(this.bar, '[data-lb-proxy]');
      const title = clean(one(main, '.product-title')?.textContent);
      setText(one(this.bar, '[data-lb-title]'), title);
      const price = displayText(one(main, '[data-block-type="price"] sale-price') || one(main, '[data-block-type="price"] .price-list'));
      setText(one(this.bar, '[data-lb-price]'), price);
      const variantMedia = this.variantMediaId ? all(main, '.product-gallery__media').find(media => media.dataset.mediaId === String(this.variantMediaId)) : null;
      const image = one(variantMedia, 'img') || one(main, '.product-gallery__media.is-initial img') || one(main, '.product-gallery__media img');
      const thumb = one(this.bar, '[data-lb-image]');
      if (image && thumb && thumb.src !== (image.currentSrc || image.src)) {
        thumb.removeAttribute('srcset');
        thumb.src = image.currentSrc || image.src;
      }
      const disabled = original.disabled || original.getAttribute('aria-disabled') === 'true' || original.getAttribute('aria-busy') === 'true';
      proxy.disabled = disabled;
      proxy.setAttribute('aria-busy', String(original.getAttribute('aria-busy') === 'true'));
      setText(proxy, displayText(original));
      const style = getComputedStyle(original);
      for (const key of ['--button-background', '--button-text-color']) proxy.style.setProperty(key, style.getPropertyValue(key));
      const sourceVisible = visible(original);
      const passed = sourceVisible && original.getBoundingClientRect().bottom <= this.headerBottom();
      this.show(this.mobile.matches && passed && !this.overlayOpen());
    }
    descriptionFold(main) {
      const productName = clean(one(main, '.product-title')?.textContent).replace(/\s+Eau de Parfum$/i, '');
      const title = this.dataset.descriptionPrefix && productName ? `${this.dataset.descriptionPrefix} ${productName}` : this.dataset.descriptionTitle;
      all(main, '[data-block-type="description"]').forEach(block => {
        const existing = this.folded.get(block);
        if (existing) { setText(one(existing.fold, '[data-lb-fold-title]'), title); return; }
        if (one(block, 'details')) return;
        const prose = one(block, '.prose');
        if (!prose || one(prose, 'input,select,textarea,button,iframe,form')) return;
        const marker = document.createComment('lb-description-home');
        prose.before(marker);
        const fold = this.makeFold(title);
        one(fold, '.accordion__content').append(prose);
        block.append(fold);
        this.folded.set(block, { prose, marker, fold });
      });
    }
    summary(list) {
      if (!this.sizeSummary) {
        this.sizeSummary = document.createElement('p');
        this.sizeSummary.className = 'lb-size-summary lb-mobile-purchase-only';
        this.scentSummary = document.createElement('p');
        this.scentSummary.className = 'lb-scent-summary lb-mobile-purchase-only';
        this.owned.add(this.sizeSummary); this.owned.add(this.scentSummary);
      }
      for (const node of [this.sizeSummary, this.scentSummary]) if (node.parentNode !== list) list.append(node);
      // Excerpts are read from the original editable description and note cells, never stored separately.
      const paragraphs = all(this.main, '[data-block-type="description"] .prose p');
      const size = paragraphs.map(p => clean(p.textContent)).find(text => text.length < 100 && /\b\d+(?:\.\d+)?\s*(?:ml|fl\.?\s*oz|oz\.)\b/i.test(text)) || '';
      setText(this.sizeSummary, size);
      this.sizeSummary.hidden = !size;
      const notes = all(one(this.main, '.notes-section'), 'tr td:first-child').map(td => clean(td.textContent)).filter(Boolean);
      setText(this.scentSummary, notes.join(' · '));
      this.scentSummary.hidden = !notes.length;
    }
    upsell(list) {
      // Select only top-level app widgets belonging to this product section, never recommendations/cart widgets.
      const widgets = all(this.main, '[data-essential-upsell-element]').filter(node => !node.parentElement.closest('[data-essential-upsell-element]'));
      for (const widget of widgets) {
        const heading = all(widget, 'h1,h2,h3,h4,h5,h6,p,span,div').find(node => /^(Your Signature Set|Complete Your Set)$/i.test(clean(node.textContent)));
        const existing = this.sets.get(widget);
        if (existing) {
          if (heading) {
            if (existing.heading !== heading) {
              existing.heading.hidden = existing.wasHidden;
              existing.heading = heading;
              existing.wasHidden = heading.hidden;
            }
            setText(one(existing.wrapper, '[data-lb-fold-title]'), clean(heading.textContent));
            heading.hidden = true;
          }
          continue;
        }
        // Unknown app layouts remain untouched, preserving all offers and controls.
        if (!heading) continue;
        const marker = document.createComment('lb-upsell-home');
        widget.before(marker);
        const wrapper = document.createElement('div');
        wrapper.className = 'lb-set-wrapper';
        const title = clean(heading.textContent);
        const fold = this.makeFold(title, /^Your Signature Set$/i.test(title));
        wrapper.append(fold);
        const wasHidden = heading.hidden;
        heading.hidden = true;
        list.append(wrapper);
        one(fold, '.accordion__content').append(widget);
        this.sets.set(widget, { marker, wrapper, heading, wasHidden });
      }
      for (const [widget, state] of this.sets) {
        if (!widget.isConnected) { state.wrapper.remove(); state.marker.remove(); this.sets.delete(widget); }
      }
    }
    restoreMobile() {
      for (const { prose, marker, fold } of this.folded.values()) {
        if (marker.parentNode) marker.replaceWith(prose);
        fold.remove();
      }
      this.folded.clear();
      for (const [widget, state] of this.sets) {
        state.heading.hidden = state.wasHidden;
        if (state.marker.parentNode) state.marker.replaceWith(widget);
        state.wrapper.remove();
      }
      this.sets.clear();
    }
    purchase() {
      // Re-resolve after variant rerenders, then click the real button for app compatibility.
      this.product();
      if (!this.original || this.original.disabled || this.original.getAttribute('aria-busy') === 'true') return;
      if (this.lastClick && performance.now() - this.lastClick < 800) return;
      if (!this.form.checkValidity()) {
        const invalid = all(this.form, ':invalid')[0] || all(document, ':invalid').find(el => el.form === this.form);
        invalid?.scrollIntoView({ behavior: 'auto', block: 'center' });
        invalid?.focus();
        this.form.reportValidity();
        return;
      }
      this.lastClick = performance.now();
      this.original.click();
      this.schedule();
    }
    collection() {
      const grid = one(document, '.shopify-section--main-collection');
      if (!grid || !this.dataset.collection || !this.dataset.product) { this.show(false); return; }
      const look = one(document, '.shopify-section--shop-the-look');
      let starts = [];
      if (look && innerWidth >= 1000) starts = all(look, '.shop-the-look__item-content').filter(visible);
      else if (look) starts = all(look, '.v-stack > button.button').filter(visible);
      const gridTop = grid.getBoundingClientRect().top;
      const start = Math.min(gridTop, ...starts.map(el => el.getBoundingClientRect().top));
      const lastProduct = all(grid, 'product-card').filter(visible).at(-1);
      const lastProductReachedTop = (lastProduct ? lastProduct.getBoundingClientRect().top : grid.getBoundingClientRect().bottom) <= this.headerBottom();
      const caption = all(document, '.shopify-section--image-with-text-block .prose').find(el =>
        (grid.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) && visible(el));
      const captionEntered = caption && caption.getBoundingClientRect().top < innerHeight;
      const shoppingVisible = start < innerHeight && !lastProductReachedTop && !captionEntered;
      this.show(!shoppingVisible && !this.overlayOpen());
    }
  }
  customElements.define('lb-purchase-features', PurchaseFeatures);
})();
